import React, { useLayoutEffect } from "react";
import {
  Box,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
  Divider,
  Popover,
} from "@mui/material";
import { MenuIcon, SettingsIcon, LogoutIcon, LoginIcon } from "../../icons";
import { observer } from "mobx-react";
import { useRootStore } from "../../store/contexts";
import CssBaseline from "@mui/material/CssBaseline";
import { SideBar } from "./SideBar";
import { NavigationBar } from "./NavigationBar";
import { HeaderBar } from "./HeaderBar";
import { getAppBar } from "./appBar";
import { ToolBar } from "./ToolBar";
import { ErrorDialog } from "../controls";
import { SessionExpiredDialog } from "../../auth/components/SessionExpiredDialog";
import { UploadProgressDialog } from "../components/UploadProgressDialog";
import { GlobalSnackbar } from "./GlobalSnackbar";
import { BreadcrumbsBar } from "./BreadcrumbsBar";
import { ConnectionStatusIndicator } from "../components/ConnectionStatusIndicator";
import { NotificationBell } from "../components/NotificationBell";
import { ThemeToggle } from "../components/ThemeToggle";
import { SystemMonitor } from "../components/SystemMonitor";
import { AuthAvatar } from "../components/AuthAvatar";
import { LAYOUT_CONSTANTS } from "./constants";
import { InnerDrawer } from "./InnerDrawer";
import {
  SettingsDrawer,
  SettingsSection,
} from "../../settings/components/SettingsDrawer";
// TEMPORAL: iO (Asistente de IA) oculto hasta que funcione bien. Descomentar junto con el botón/drawer abajo.
// import { AiAssistantButton, AiAssistantDrawer } from "../aiAssistant";

const drawerWidth = 240;
const closedDrawerWidth = 73;
const firstBarTop = LAYOUT_CONSTANTS.APPBAR_HEIGHT_DENSE;

export type NavigationElementsType = {
  links: Record<string, string>[];
  navigateTo?: string;
  navigateToHash?: string;
  onNavigatedTo?: (id: string) => void;
};

export type PageLayoutProps = {
  children?: React.ReactElement | React.ReactElement[];
  navigationElements?: NavigationElementsType;
  mainToolbox?: React.ReactElement;
  secondaryToolbox?: React.ReactElement;
  showHeaderBar?: boolean;
  headerChildren?: React.ReactElement | React.ReactElement[];
};

export const PageLayout: React.FC<PageLayoutProps> = observer(
  ({
    children,
    navigationElements,
    mainToolbox,
    secondaryToolbox,
    showHeaderBar = false,
    headerChildren = <div />,
  }) => {
    const [anchorElLanguage, setAnchorElLanguage] =
      React.useState<null | HTMLElement>(null);

    const [headerBarHeight, setHeaderBarHeight] = React.useState<number>(0);
    const breadcrumbsHeight = 4;
    const [mainToolboxHeight, setMainToolboxHeight] = React.useState<number>(0);
    const [navigationHeight, setNavigationHeight] = React.useState<number>(0);
    const [listToolboxHeight, setListToolboxHeight] = React.useState<number>(0);
    const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
      null
    );
    const [settingsDrawerOpen, setSettingsDrawerOpen] = React.useState(false);
    const [settingsSection, setSettingsSection] =
      React.useState<SettingsSection>("profile");

    const rootStore = useRootStore();
    const { prefStore, authStore, langStore } = rootStore;

    const AppBar = getAppBar(closedDrawerWidth, drawerWidth);

    const breadcrumbsTop = firstBarTop + headerBarHeight;
    const mainToolboxTop = breadcrumbsTop + breadcrumbsHeight;
    const navigationTop = mainToolboxTop + mainToolboxHeight;
    const listToolboxTop = navigationTop + navigationHeight;
    const contentTop = listToolboxTop + listToolboxHeight;

    useLayoutEffect(() => {
      prefStore.setCurrentContentTop(contentTop);
    }, [prefStore, contentTop]);

    const handleSideBarOpen = () => {
      prefStore.setSideBarOpen(true);
    };

    const handleSideBarClose = () => {
      prefStore.setSideBarOpen(false);
    };

    const handleClickUserMenu = (itemName: string) => {
      if (itemName === langStore.logInLabel) {
        rootStore.router.goTo("/login");
      } else if (itemName === langStore.settingsLabel) {
        setSettingsSection("preferences");
        setSettingsDrawerOpen(true);
        handleCloseUserMenu();
      } else if (itemName === langStore.changePasswordLabel) {
        rootStore.router.goTo("/change-password");
      } else if (itemName === langStore.logOutLabel) {
        authStore.logout();
        rootStore.router.replace("/");
      } else if (itemName === "Toggle theme") {
        handleCloseUserMenu();
        prefStore.toggleTheme();
      }
    };

    const handleCloseUserMenu = () => {
      setAnchorElUser(null);
    };

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorElUser(event.currentTarget);
    };

    const handleCloseSettingsDrawer = () => {
      setSettingsDrawerOpen(false);
    };

    const handleSettingsSectionChange = (section: SettingsSection) => {
      setSettingsSection(section);
    };

    const handleOpenLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorElLanguage(event.currentTarget);
    };

    const handleClickLanguageMenu = (itemName: string) => {
      const languageKey =
        itemName === langStore.languageLabels.en
          ? "en"
          : itemName === langStore.languageLabels.pt
          ? "pt"
          : "es";
      langStore.setLanguage(languageKey);
      // Persistir en user.language (self-service) si hay usuario logueado.
      // Best-effort: el cambio en sesión ya se aplicó vía langStore.
      if (authStore.me) {
        Promise.resolve(authStore.setMyLanguage(languageKey)).catch(() => {});
      }
      handleCloseLanguageMenu();
    };

    const handleCloseLanguageMenu = () => {
      setAnchorElLanguage(null);
    };

    const updateHeaderBarHeight = (height: number | undefined) => {
      if (height) {
        setHeaderBarHeight(height);
      }
    };

    const updateMainToolboxHeight = (height: number | undefined) => {
      if (height) {
        setMainToolboxHeight(height);
      }
    };

    const updateNavigationHeight = (height: number | undefined) => {
      if (height) {
        setNavigationHeight(height);
      }
    };

    const updateListToolboxHeight = (height: number | undefined) => {
      if (height) {
        setListToolboxHeight(height);
      }
    };

    const headerBar = showHeaderBar ? (
      <HeaderBar
        updateHeight={updateHeaderBarHeight}
        headerChildren={headerChildren}
      />
    ) : (
      <div />
    );

    const breadcrumbsBar = <BreadcrumbsBar />;

    let mainToolboxBar;
    if (mainToolbox) {
      mainToolboxBar = (
        <ToolBar
          updateHeight={updateMainToolboxHeight}
          barTop={listToolboxTop}
          drawerWidth={drawerWidth}
          closedDrawerWidth={closedDrawerWidth}
          sideBarOpen={prefStore.sideBarOpen}
        >
          {mainToolbox}
        </ToolBar>
      );
    }

    let navigationBar;
    if (navigationElements) {
      navigationBar = (
        <ToolBar
          updateHeight={updateNavigationHeight}
          barTop={navigationTop}
          drawerWidth={drawerWidth}
          closedDrawerWidth={closedDrawerWidth}
          sideBarOpen={prefStore.sideBarOpen}
        >
          <NavigationBar
            links={navigationElements.links}
            contentTop={contentTop}
            navigateTo={navigationElements.navigateTo}
            navigateToHash={navigationElements.navigateToHash}
            onNavigatedTo={navigationElements.onNavigatedTo}
          />
        </ToolBar>
      );
    }

    let listToolboxElement;
    if (secondaryToolbox) {
      listToolboxElement = (
        <ToolBar
          updateHeight={updateListToolboxHeight}
          barTop={listToolboxTop}
          drawerWidth={drawerWidth}
          closedDrawerWidth={closedDrawerWidth}
          sideBarOpen={prefStore.sideBarOpen}
        >
          {secondaryToolbox}
        </ToolBar>
      );
    }

    return (
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          open={prefStore.sideBarOpen && authStore.status === "logged"}
        >
          <Toolbar variant="dense">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleSideBarOpen}
              edge="start"
              sx={{
                ...((prefStore.sideBarOpen ||
                  authStore.status !== "logged") && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>

            {authStore.logoUrl && (
              <img
                src={authStore.logoUrl}
                alt={authStore.me?.company?.name || "Company logo"}
                style={{
                  height: LAYOUT_CONSTANTS.APPBAR_LOGO_HEIGHT,
                  maxWidth: LAYOUT_CONSTANTS.APPBAR_LOGO_MAX_WIDTH,
                  objectFit: "contain",
                  marginLeft: 16,
                }}
              />
            )}

            {headerBar}
            {breadcrumbsBar}

            <Box sx={{ flexGrow: 1 }} />
            <Box
              sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}
            >
              {/* TEMPORAL: botón iO oculto hasta que el Asistente de IA funcione bien. Descomentar para reactivar. */}
              {/* {authStore.status === "logged" && <AiAssistantButton />} */}
              <Box
                aria-label="account of current user"
                aria-controls="user-menu"
                aria-haspopup="true"
                onClick={handleOpenUserMenu}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <AuthAvatar
                  avatarData={authStore.me?.avatar_data}
                  name={authStore.me?.name || ""}
                  userId={authStore.me?.id || ""}
                  size={40}
                />
              </Box>
              <Menu
                id="user-menu"
                anchorReference="anchorPosition"
                anchorPosition={{
                  top: firstBarTop + 8,
                  left: window.innerWidth - 10,
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  sx: {
                    minWidth: 240,
                  },
                }}
              >
                {authStore.status === "logged" && (
                  <>
                    <Box
                      sx={{ px: 2, py: 1.5, backgroundColor: "action.hover" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <AuthAvatar
                          avatarData={authStore.me?.avatar_data}
                          name={authStore.me?.name || ""}
                          userId={authStore.me?.id || ""}
                          size={40}
                        />
                        <Box sx={{ overflow: "hidden" }}>
                          <Typography variant="subtitle2" noWrap>
                            {authStore.me?.name || ""}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                          >
                            {authStore.me?.email || ""}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider />

                    <MenuItem
                      onClick={() =>
                        handleClickUserMenu(langStore.settingsLabel)
                      }
                    >
                      <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{langStore.settingsLabel}</ListItemText>
                    </MenuItem>

                    <Divider />

                    <MenuItem
                      onClick={() => handleClickUserMenu(langStore.logOutLabel)}
                    >
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{langStore.logOutLabel}</ListItemText>
                    </MenuItem>
                  </>
                )}

                {authStore.status !== "logged" && (
                  <MenuItem
                    onClick={() => handleClickUserMenu(langStore.logInLabel)}
                  >
                    <ListItemIcon>
                      <LoginIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{langStore.logInLabel}</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </Box>

            {authStore.status === "logged" && <NotificationBell />}
            {authStore.status === "logged" && <ThemeToggle />}
            {authStore.status === "logged" && <ConnectionStatusIndicator />}

            {authStore.status !== "logged" && (
              <div>
                <IconButton
                  size={"large"}
                  aria-label="select language"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenLanguageMenu}
                  color={"primary"}
                >
                  <Typography sx={{ fontSize: 13 }} variant={"h6"}>
                    {langStore.languageLabels[langStore.language]}
                  </Typography>
                </IconButton>
                <Popover
                  id="menu-appbar"
                  anchorReference="anchorPosition"
                  anchorPosition={{
                    top: firstBarTop,
                    left: window.innerWidth - 10,
                  }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElLanguage)}
                  onClose={handleCloseLanguageMenu}
                >
                  {Object.values(langStore.languageLabels).map((item) => (
                    <MenuItem
                      key={item}
                      onClick={() => handleClickLanguageMenu(item)}
                    >
                      <Typography textAlign="center">{item}</Typography>
                    </MenuItem>
                  ))}
                </Popover>
              </div>
            )}
          </Toolbar>
        </AppBar>

        {mainToolboxBar}
        {navigationBar}
        {listToolboxElement}

        <SideBar
          open={prefStore.sideBarOpen}
          onClose={handleSideBarClose}
          headerHeight={firstBarTop + breadcrumbsHeight}
        />

        <Box
          component="main"
          sx={{ flexGrow: 1, p: 3 }}
          style={{ marginTop: contentTop }}
        >
          {children}
          <ErrorDialog
            open={rootStore.generalErrorOpen}
            title={rootStore.generalErrorTitle}
            description={rootStore.generalErrorDescription}
            onClose={() => {
              rootStore.generalErrorOpen = false;
            }}
          />
          <SessionExpiredDialog
            open={rootStore.authStore.sessionExpiredDialogOpen}
            onClose={() => {
              rootStore.authStore.sessionExpiredDialogOpen = false;
            }}
          />
          <UploadProgressDialog />
          <GlobalSnackbar />
          <SystemMonitor />
        </Box>

        {settingsDrawerOpen && (
          <InnerDrawer
            open={settingsDrawerOpen}
            onClose={handleCloseSettingsDrawer}
            contentTop={prefStore.getCurrentContentTop()}
          >
            <SettingsDrawer
              section={settingsSection}
              onClose={handleCloseSettingsDrawer}
              onSectionChange={handleSettingsSectionChange}
            />
          </InnerDrawer>
        )}

        {/* TEMPORAL: drawer iO oculto junto con el botón. Descomentar para reactivar. */}
        {/* {authStore.status === "logged" && <AiAssistantDrawer />} */}
      </Box>
    );
  }
);
